import {Injectable, Injector} from '@angular/core';
import {auditTime, concatMap, forkJoin, from, Observable, of, Subject, switchMap, takeWhile, throttleTime} from 'rxjs';
import {IFont} from "../../types/IFont";
import {ICachingService} from "@signage/interface/dist/ICachingService";
import {CacheItem} from "./CacheItem";
import {Expiration} from "../../interfaces/Expiration";
import {CacheJson} from "./CacheJson";
import {CacheText} from "./CacheText";
import {CacheFont} from "./CacheFont";
import {CacheDrive} from "./CacheDrive";
import {HttpClient} from "@angular/common/http";
import {lastMap} from "../../rxjs/lastMap";
import {CacheDBPlayer} from "./CacheDBPlayer";
import {CacheDBStudio} from "./CacheDBStudio";
import {CacheDropbox} from "./CacheDropbox";
import {Log, LogTag} from "../Log";
import {catchError, debounceTime, last, map, take, tap} from "rxjs/operators";



@Injectable({
  providedIn: 'root'
})
export class CachingService implements ICachingService {
    protected httpClient: HttpClient;
    protected gpu: boolean = true;
    protected studio: boolean;
    protected _businessId: number;
    protected _businessDomain: string;
    protected cacheMap = {};
    protected cacheDB = {};


    protected abortFetch;

    cachingNow = {};

    foldersMap = {};
    lastUpdated$ = new Subject<any>();

    worker;
    workerLoaded$ = new Subject<any>();
    workerSaved$ = new Subject<any>();

    constructor(protected injector: Injector) {
        this.httpClient = injector.get(HttpClient);

    }

    init(gpu: boolean, studio: boolean): Observable<any> {
        this.gpu = gpu;
        this.studio = studio;
        this.abortFetch = new AbortController();

        if (studio) {
            return of('init');
        }

        this.worker = new Worker('./workers/caching.worker.js', { type: 'module' });
        this.worker.onmessage = ({ data }) => {
            switch (data.command) {
                case "loaded":
                    this.foldersMap = data.foldersMap;
                    this.workerLoaded$.next(data);
                    this.workerLoaded$.complete();
                    break;
                case "saved":
                    for (let folder in data.deletedMap) {
                        const folderList = data.deletedMap[folder];
                        for (const url of folderList) {
                            console.log(url);
                            delete this.foldersMap[folder][url];
                        }
                    }
                    this.workerSaved$.next(data);
                    break;
            }

        };

        // every 1 hour
        this.lastUpdated$.pipe(
            auditTime(3600000),
            switchMap( a => {
                return this.save();
            })
        ).subscribe();


        const data = {
            command: "load"
        };

        this.worker.postMessage(data);
        return this.workerLoaded$;
    }

    abort() {
        this.abortFetch.abort();
        this.abortFetch = new AbortController();
    }

    get businessId(): number {
        return this._businessId;
    }

    set businessId(value: number) {
        this._businessId = value;
    }

    get businessDomain(): string {
        return this._businessDomain;
    }

    set businessDomain(value: string) {
        this._businessDomain = value;
    }

    updateLastUsed(folder: string, url: string): void {
        let folderInfo = this.foldersMap[folder];
        if (folderInfo==null) {
            this.foldersMap[folder] = folderInfo = {};
        }
        if (folderInfo[url] == null) {
            folderInfo[url] = {};
            folderInfo[url].created = new Date().getTime();
        }
        folderInfo[url].used = new Date().getTime();
        this.lastUpdated$.next('update');
    }



    private request(url: string, folder: string, expiration: number, className, proxyUrl: string): Observable<any> {
        let cacheUrl = this.cacheMap[url];
        if (cacheUrl == null) {
            cacheUrl = this.cacheMap[url] = new className(this, this.httpClient, url, folder, proxyUrl);
        }
        return cacheUrl.request(expiration);
    }

    requestUrl(url: string, folder: string, expiration: number): Observable<any> {
        this.updateLastUsed(folder, url);

        if (this.cachingNow[url]) {
            //console.log('caching already started');
            return of(url);
        }
        this.cachingNow[url] = true;

        const obs$ = of('a');
        return obs$.pipe(
            switchMap(a => from(caches.match(url))),
            switchMap(cachedResponse => {
                if (cachedResponse) {
                    //console.log('exist in cache. ' + url);
                    return of(url);
                }
                //console.log('not exist in cache. ' + url);

                this.cacheUrl(folder, url);
                return of(url);
            })
        )
    }

    requestText(url: string, folder: string, expiration: number, proxyUrl: string = null): Observable<any> {
        return this.request(url, folder, expiration, CacheText, proxyUrl);
    }

    requestJson(url: string, folder: string, expiration: number, proxyUrl: string = null): Observable<any> {
        return this.request(url, folder, expiration, CacheJson, proxyUrl);
    }

    requestFont(font: IFont): Observable<any> {
        if (font.fontFamily == null || font.fontFamily == "") {
            console.error('font.fontFamily is empty')
            return of(null);
        }

        const fontKey = CacheFont.getFontKey(font);
        let fileName = fontKey + '.ttf';
        const url = 'https://galaxy.signage.me/TTF/' + fileName;
        let cacheUrl = this.cacheMap[url];
        if (cacheUrl == null) {
            cacheUrl = this.cacheMap[url] = new CacheFont(this, this.httpClient, url, 'fonts', fontKey);
        }
        return cacheUrl.request(Expiration.Never);
    }

    getDriveUrl(token: string, id: string, expiration: number): Observable<any> {
        let cacheUrl: CacheItem = this.cacheMap[id];
        if (cacheUrl == null) {
            cacheUrl = this.cacheMap[id] = new CacheDrive(this, this.httpClient, token, id, 'drive');
        }
        return cacheUrl.request(Expiration.Never);
    }

    getDropboxUrl(token: string, id: string, expiration: number): Observable<any> {
        let cacheUrl: CacheItem = this.cacheMap[id];
        if (cacheUrl == null) {
            cacheUrl = this.cacheMap[id] = new CacheDropbox(this, this.httpClient, token, id, 'dropbox');
        }
        return cacheUrl.request(Expiration.Never);
    }

    getRecord(table: string, id: number): Observable<any> {
        const key = table + '.' + id;
        let cacheDB: any = this.cacheDB[key];
        if (cacheDB == null) {
            if (this.studio) {
                cacheDB = new CacheDBStudio(table, id);
                cacheDB.setData(of(null));
            } else {
                cacheDB = new CacheDBPlayer(this, this.httpClient, this._businessDomain, this.businessId, table, id);
            }
            this.cacheDB[key] = cacheDB;
        }
        if (this.studio) {
            return cacheDB.getData();
        } else {
            return cacheDB.request(Expiration.Now);
        }

    }

    setRecord(table: string, id: number, data: Observable<any>): void {
        const key = table + '.' + id;
        if (this.studio) {
            let cacheDB: CacheDBStudio = this.cacheDB[key];
            if (cacheDB == null) {
                cacheDB = new CacheDBStudio(table, id);
                this.cacheDB[key] = cacheDB;
            }
            cacheDB.setData(data);
        }
    }

    modifyRecords(table, ids): void { // when use do save
        const logInfo = Log.getLogInfo("CachingService", "modifyRecords", [table, ids]);
        Log.d(logInfo, LogTag.CACHE, '');
        if (table == 'player_data') {
            table = 'scenes';
        }
        ids.forEach(id => {
            const key = table + '.' + id;
            let cacheDB: CacheDBPlayer = this.cacheDB[key];
            if (cacheDB != null) {
                cacheDB.modified = true;
            }
        })
    }

    setModifyAll() {  // when socket connection lost. cause we don't know what records got changed when socket was off
        const logInfo = Log.getLogInfo("CachingService", "setModifyAll", []);
        Log.d(logInfo, LogTag.CACHE, '');
        for (let key in this.cacheDB) {
            let cacheDB: CacheDBPlayer = this.cacheDB[key];
            cacheDB.modified = true;
        }
    }

    clearModified() {
        const logInfo = Log.getLogInfo("CachingService", "clearModified", []);
        Log.d(logInfo, LogTag.CACHE, '');
        for (let key in this.cacheDB) {
            let cacheDB: CacheDBPlayer = this.cacheDB[key];
            if (cacheDB.modified) {
                cacheDB.modified = false;
                cacheDB.data = null;
            }
        }
    }


    deleteCachedUrl(folder, url): Observable<any> {
        return from(caches.open(folder)).pipe(
            switchMap(cache => {
                return from(cache.delete(url));
            })
        )
    }

    cacheUrl(folder, url) {
        //console.log('start caching...');
        fetch(url).then(response => {
            if (response.status == 200) {
                const clonedResponse = response.clone();
                caches.open(folder).then(cache => {
                    cache.put(url, clonedResponse).then(a => {
                        //console.log('put completed')
                        delete this.cachingNow[url];
                    }).catch(err => {
                        delete this.cachingNow[url];
                    })
                })
            } else {
                delete this.cachingNow[url];
            }
        })
    }



    getResource(resourceId: string): Observable<any> {
        const logInfo = Log.getLogInfo("CachingService", "getResource", [resourceId]);
        return this.getRecord("resources", Number(resourceId)).pipe(
            switchMap(resource => {
                const url = 'https://s3-us-west-2.amazonaws.com/oregon-signage-resources/business' + this.businessId + '/resources/' + resourceId + '.' + (<any>resource).resource_type;
                if (resource == null || (<any>resource).changelist_id == null) {
                    return of(null);
                }

                this.updateLastUsed('resources', url);

                const ext = url.substring(url.lastIndexOf('.') + 1);
                if (this.studio && ext == 'mp4') {
                    Log.d(logInfo, LogTag.CACHE, 'Do not cache video in studio');
                    return of(url);
                }


                if (this.cachingNow[url]) {
                    //console.log('caching already started');
                    return of(url);
                }
                this.cachingNow[url] = true;

                const obs$ = ((<any>resource).updated) ? this.deleteCachedUrl('resources', url) : of(true);
                return obs$.pipe(
                    switchMap(a => from(caches.match(url))),
                    switchMap(cachedResponse => {
                        if (cachedResponse) {
                            //console.log('exist in cache');
                            return of(url);
                        }
                        //console.log('not exist in cache');

                        this.cacheUrl('resources', url);
                        return of(url);
                    })
                )

            })
        )
    }

    getUrlParam(url, param) {
        const i0 = url.indexOf('?');
        const i1 = url.indexOf(param, i0) + 1;
        const i2 = url.indexOf("&", i1);
        const i3 = (i2 == -1) ? url.length : i2;
        return url.substring(i1 + param.length, i3);
    }



    save(): Observable<boolean> {
        const data = {
            command: "save",
            foldersMap: this.foldersMap
        };
        this.worker.postMessage(data); // Send data to the worker
        return this.workerSaved$.asObservable();

    }







    deleteOldCaching(): Observable<any> {
        return of(null);
        /*
        return new Observable<any>(sub => {
            const logInfo = Log.getLogInfo("CachingService", "deleteOldCaching", []);
            const sortItems = this.allCacheItems.sort((a, b) => (a.lastUsed < b.lastUsed) ? -1 : 1);
            const now = new Date().getTime();
            let total = 0;
            let count = 0;

            let maxPeriod = 30 * 24 * 60 * 60 * 1000;
            if (localStorage['caching'] != null) {
                const caching = JSON.parse(localStorage['caching']);
                maxPeriod = caching.maxPeriod;
            }

            sortItems.forEach(item => {
                if (now - item.lastUsed > maxPeriod) {
                    Log.d(logInfo, LogTag.CACHE, "DELETE: " + item.request.url)
                    total++;
                    item.cache.delete(item.request).then(res => {
                        count++;
                        if (total == count) {
                            sub.next("finish");
                            sub.complete();
                        }
                    });
                } else {
                    Log.d(logInfo, LogTag.CACHE, "SKIP: " + item.request.url)
                }
            })
            if (total == 0) {
                sub.next("finish");
                sub.complete();
            }
        })

         */
    }
}
